import React, { useEffect, useState } from 'react';
import './AdvertiserDashboardHome.css';
import { useSelector } from 'react-redux';
import { getToken } from '../../../../features/token/tokenSaver';
import ScreenMapView from '../ScreenMapView/ScreenMapView';
import { Link, useNavigate } from 'react-router-dom';
import { platformAgreementPath } from '../../../../utils/platformAgreementUrl';
import { useAdvertiserOnboarding } from '../../AdvertiserOnboarding/AdvertiserOnboardingContext';
import { useAdvertiserAccountContext } from '../../../../context/AdvertiserAccountContext';
import { useAdvertiserHomeQuery } from '../../../../query/dashboardQueries';
import '../../AdvertiserOnboarding/AdvertiserOnboarding.css';

function parseAdvertiserId(token) {
    try {
        return JSON.parse(atob(token.split('.')[1]))?.id ?? null;
    } catch {
        return null;
    }
}

export default function AdvertiserDashboardHome() {
    const token = useSelector(getToken);
    const advertiserId = parseAdvertiserId(token);
    const navigate = useNavigate();
    const onboarding = useAdvertiserOnboarding();
    const { account } = useAdvertiserAccountContext();
    const { data: homeData } = useAdvertiserHomeQuery({ advertiserId });

    const monthlyFee = Number(account?.slotCost) > 0 ? Number(account.slotCost) : 500;
    const campaigns = homeData?.campaigns || [];
    const totalPlays = homeData?.totalPlays || homeData?.data?.impressions || 0;
    const attributedSpendTotal = homeData?.attributedSpendTotal || 0;
    const activeCampaignsCount = campaigns.filter((c) => c.StatusId === 1).length;
    const [showOnboardingEmpty, setShowOnboardingEmpty] = useState(false);

    useEffect(() => {
        if (Boolean(account?.showOnboardingTour) && campaigns.length === 0) {
            setShowOnboardingEmpty(true);
        } else if (campaigns.length > 0) {
            setShowOnboardingEmpty(false);
        }
    }, [account?.showOnboardingTour, campaigns.length]);

    return (
        <div className='advertiserDashboardContainer'>
            <span className='advertiserDashboardHometitle'>Welcome!</span>
            {showOnboardingEmpty && onboarding?.tourActive && (
                <div className="advertiser-onboarding-empty-state">
                    <p>Ready to run your first campaign? Follow the guided tour or jump straight to campaign creation.</p>
                    <button
                        type="button"
                        className="advertiser-onboarding-btn advertiser-onboarding-btn-primary"
                        onClick={() => {
                            onboarding.jumpToStep('create_campaign');
                        }}
                    >
                        Create first campaign
                    </button>
                </div>
            )}
            <div className='atAGlanceItemsContainer' data-onboarding-id="home-map">
                <div className='atAGlanceTodayImpressions'>
                    <span className='atAGlanceTodayImpressionsTitle'>Total plays</span>
                    <span className='atAGlanceTodayImpressionsCount'>{totalPlays.toLocaleString()}</span>
                    <div id="borderRight"></div>
                </div>
                <div className='atAGlanceTodayImpressions'>
                    <span className='atAGlanceTodayImpressionsTitle'>Attributed spend</span>
                    <span className='atAGlanceTodayImpressionsCount'>${attributedSpendTotal.toFixed(2)}</span>
                    <div id="borderRight"></div>
                </div>
                <div className='atAGlanceTodayImpressions'>
                    <span className='atAGlanceTodayImpressionsTitle'>Active Campaigns</span>
                    <span className='atAGlanceTodayImpressionsCount'>{activeCampaignsCount}</span>
                </div>
            </div>
            <p style={{ fontFamily: 'Inter-Regular', margin: '0 0 1rem', color: 'rgba(0,0,0,0.65)' }}>
                Choose screens when creating a campaign, or{' '}
                <Link to="/advertiserDashboard/prepay" style={{ color: '#C52F00' }}>prepay for next month</Link>{' '}
                to reserve your spot without uploading creative yet.
            </p>
            <ScreenMapView />
            <div className='topCampaignsContainer'>
                <span className='topCampaignsTitle'>My Campaigns</span>
                <span className='topCampaignsSubTitle' onClick={() => {
                    navigate('/advertiserDashboard/campaigns');
                }}>See All</span>
                <table className='topCampaignsTable'>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Campaign</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.isArray(campaigns) && campaigns.map((campaign, i) => (
                            <tr key={campaign.AdvertisementId || i}>
                                <td>{campaign.AdvertisementName}</td>
                                <td>Campaign #{i + 1}</td>
                                <td>
                                    <span className={`campaignStatusBadge status-${campaign.StatusId}`}>
                                        {campaign.StatusId === 1 ? 'Active' : campaign.StatusId === 3 ? 'Pending' : 'Inactive'}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <footer className="advertiser-dashboard-home-footer">
                <Link
                    to={platformAgreementPath(monthlyFee)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="advertiser-dashboard-agreement-link"
                >
                    INVERT Advertising Agreement
                </Link>
            </footer>
        </div>
    );
}
